import Button from '@/common/components/Button'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import ListFilters from '@/common/components/ListFilters'
import LoadingSpinner from '@/common/components/LoadingSpinner'
import ResumeItem from '@/common/components/ResumeItem'
import Table from '@/common/components/Table'
import { DatePickerWithRange } from '@/common/components/ui/dateRangePicker'
import useCompany from '@/common/hooks/queries/useCompany'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterDate } from '@/common/hooks/useFilterDate'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import { formatCurrency } from '@/common/utils/formatters'
import { bankingDepositHasFiscalDocumentOptions } from '@/features/privateCompanies/constants/bankingDeposits/bankingDepositHasFiscalDocument.constant'
import { bankingDepositsTableColumns } from '@/features/privateCompanies/constants/bankingDeposits/bankingDepositsTableColumns.constant'
import { bankingDepositStatusOptions } from '@/features/privateCompanies/constants/bankingDeposits/bankingDepositStatus.constant'
import { useBankingDeposits } from '@/features/privateCompanies/hooks/bankingDeposits/useBankingDeposits'
import { useClientCompanyPaymentMethods } from '@/features/privateCompanies/hooks/bankingDeposits/useClientCompanyPaymentMethods'
import { BankingDepositsTransformer } from '@/features/privateCompanies/services/bankingDeposits/bankingDepositsTransformer'
import { ClientPaymentMethodData } from '@/features/privateCompanies/types/bankingDeposits/paymentMethods.type'
import { Link, useParams } from 'react-router-dom'

const BankingDepositsList = () => {
  const { id: companyId } = useParams();

  const { filterParams, setFilterParams } = useFilterParams();

  const { date, formattedDate } = useFilterDate(filterParams);

  const [searchPaymentMethod, setSearchPaymentMethod] = useDebounce('');

  const {
    data: company,
    isPending: isCompanyPending,
  } = useCompany({ id: companyId || '' });

  const {
    data: paymentMethodsOptions,
  } = useClientCompanyPaymentMethods({
    filters: {
      companyId,
      search: searchPaymentMethod,
    },
    mapper: Mapper.mapToOptions<ClientPaymentMethodData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
  });

  const {
    data: bankingDeposits,
    isPending: bankingDepositsIsPending,
    isFetching: bankingDepositsIsFetching,
    error: bankingDepositsError = {} as any,
    refetch: refetchBankingDeposits,
    meta: bankingDepositsMeta,
  } = useBankingDeposits({
    filters: {
      companyId,
      page: Number(filterParams.page) || 1,
      status: filterParams.status?.value,
      mainFiscalDocumentPresent: filterParams?.mainFiscalDocumentPresent?.value,
      paymentMethod: filterParams?.paymentMethod?.value,
      createdAtGt: formattedDate.from,
      createdAtLt: formattedDate.to,
    },
    mapper: Mapper.mapWithTransformer({ transformer: BankingDepositsTransformer.tableTransformer() }),
  });

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-192px)]"
      statusCode={bankingDepositsError?.status}
      refetchFunction={refetchBankingDeposits}
    >
      <div className="flex flex-col gap-6">
        <div className='flex flex-col gap-4'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              <ResumeItem
                label='Limite disponível (Pós-pago)'
                value={company?.remaining_credit ? formatCurrency(Number(company?.remaining_credit)) : '--'}
                isLoading={isCompanyPending}
              />

              <ResumeItem
                label='Saldo disponível (Pré-pago)'
                value={company?.remaining_balance ? formatCurrency(Number(company?.remaining_balance)) : '--'}
                valueClassName='text-valid'
                isLoading={isCompanyPending}
              />
            </div>

            <div className='flex items-center gap-2'>
              <LoadingSpinner isLoading={bankingDepositsIsFetching} />
              <Link to="create">
                <Button
                  name="create_banking_deposit"
                  operationKlassToCheckPermission="Admin::Banking::Deposits::Create"
                >
                  Adicionar Saldo (Pré-pago)
                </Button>
              </Link>
            </div>
          </div>

          <div className='flex items-center gap-2'>
            <DatePickerWithRange
              date={date}
              setDate={(date) => setFilterParams((prev) => ({ ...prev, date }))}
              placeholder='Calendário'
            />

            <ListFilters
              filters={[
                {
                  className: '',
                  placeholder: 'Status',
                  options: [{ label: 'Todos', value: '' }, ...bankingDepositStatusOptions],
                  onChange: (status) => setFilterParams((prev) => ({ ...prev, status })),
                  value: filterParams.status,
                },
                {
                  className: '',
                  placeholder: 'Possui Nota',
                  options: [{ label: 'Todos', value: '' }, ...bankingDepositHasFiscalDocumentOptions],
                  onChange: (mainFiscalDocumentPresent) => setFilterParams((prev) => ({ ...prev, mainFiscalDocumentPresent })),
                  value: filterParams.mainFiscalDocumentPresent,
                },
                {
                  className: '',
                  placeholder: 'Via',
                  options: [{ label: 'Todos', value: '' }, ...paymentMethodsOptions],
                  onChange: (paymentMethod) => setFilterParams((prev) => ({ ...prev, paymentMethod })),
                  onInputChange: setSearchPaymentMethod,
                  value: filterParams.paymentMethod,
                },
              ]}
            />
          </div>
        </div>

        <div data-testid="banking_deposits_table">
          <Table
            className="max-h-[calc(100vh-350px)]"
            columns={bankingDepositsTableColumns}
            isLoadingData={bankingDepositsIsPending}
            rows={bankingDeposits}
            totalPages={bankingDepositsMeta.total_pages}
            totalCount={bankingDepositsMeta.total}
            hasPagination
            page={filterParams.page || 1}
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
            rowEmptyValue="--"
          />
        </div>
      </div>
    </ErrorMessageWrapper>
  )
}

export default BankingDepositsList
